import type { TournamentsApi } from '@/v2/api/metadata/tournaments'
import type { ContentTab, DialogTabSaveContext } from '@/v2/framework/dialog/types'
import {
  buildSupplementalNamesPatch,
  syncSupplementalNamesBaseline,
} from '@/v2/kits/supplemental-names'
import { createInheritableTabSave } from '@/v2/kits/tenant-inheritance'
import { tournamentStatusOptions } from '../../../../shared'
import { notifySuccess, tournamentId } from '../shared'

const mainTabChildren: ContentTab['children'] = [
  {
    type: 'select-field',
    name: 'status',
    label: 'Status',
    required: true,
    placeholder: 'Select status',
    options: tournamentStatusOptions,
    inheritance: {
      computedKey: 'computedStatus',
      parentKey: 'parentStatus',
    },
  },
  {
    type: 'select-field',
    name: 'sportId',
    label: 'Sport',
    disabled: true,
    options: async ({ values }) => [
      { value: values.sportId, title: String(values.sportName ?? '') },
    ],
  },
  {
    type: 'select-field',
    name: 'categoryId',
    label: 'Category',
    disabled: true,
    options: async ({ values }) => [
      { value: values.categoryId, title: String(values.categoryName ?? '') },
    ],
  },
  {
    type: 'select-field',
    name: 'sourceName',
    label: 'Origin name',
    disabled: true,
    options: async ({ values }) => [
      { value: values.sourceName ?? '', title: String(values.sourceName ?? '') },
    ],
  },
  {
    type: 'text-field',
    name: 'name',
    label: 'Name',
    placeholder: 'Enter name',
    inheritance: {
      computedKey: 'computedName',
      parentKey: 'parentName',
    },
    validate: (value, values) => {
      if (values.isTenantSourceOfEntity && !String(value ?? '').trim()) {
        return 'Required'
      }
      return undefined
    },
  },
  {
    type: 'text-field',
    name: 'info',
    label: 'Info',
    placeholder: 'Enter info',
    inheritance: {
      computedKey: 'computedInfo',
      parentKey: 'parentInfo',
    },
  },
  {
    type: 'text-field',
    name: 'code',
    label: 'Code',
    placeholder: 'Enter code',
    inheritance: {
      computedKey: 'computedCode',
      parentKey: 'parentCode',
    },
  },
  {
    type: 'text-field',
    name: 'prefix',
    label: 'Prefix',
    placeholder: 'Enter prefix',
    inheritance: {
      computedKey: 'computedPrefix',
      parentKey: 'parentPrefix',
    },
  },
]

export function createMainTab(tournaments: TournamentsApi): ContentTab {
  const save = createInheritableTabSave({
    children: mainTabChildren,
    buildExtraPatch: (ctx) => buildSupplementalNamesPatch(ctx.values, ctx.baseline),
    afterPersist: syncSupplementalNamesBaseline,
    persist: async (patch, ctx: DialogTabSaveContext) => {
      await tournaments.update(tournamentId(ctx.values), patch)
      notifySuccess()
    },
  })

  return {
    id: 'main',
    label: 'Main',
    save,
    children: [
      ...mainTabChildren,
      {
        type: 'component',
        component: () =>
          import('@/v2/kits/supplemental-names/components/SupplementalNamesFields.vue'),
        props: { mode: 'edit', entity: 'TOURNAMENT' },
      },
    ],
  }
}
